import { Component, Injector, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    ClassNames,
    Color,
    forLifeOf,
    getDynamicRouteLink,
    Interaction,
    IOptionalExportServiceConfig,
    LifecycleAware,
    Styling,
    Utilities
} from '@lsq-frontend/common-ui';
import { getRouterLinkUrlById, Icons, ResponsiveService } from '@lsq-frontend/common-ui';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { AnalyticsValue } from '../../../constants/analytics.constants';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { atlas, CUSTOMER_AGING } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    AgingSummary,
    GenerateAgingDetailReportService,
    GetAgingSummaryRequest,
    GetAgingSummaryResponse,
    GetAgingSummaryService,
    ShareReportService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { DateAsOfReportComponent } from '../base/date-as-of-report.component';
import { ReportsUserStateTypes } from '../reports.user-state-type';

@LifecycleAware()
@Component({
    selector: 'aging',
    template: `
        <div [ngClass]="{ 'min-page-width-medium-up': !(dataSource?.loading$ | async) }">
            <lsq-banner
                [titleText]="title"
                [subTitle]="(dataSource.data$ | async)?.lastUpdatedDate | lastUpdatedDate"
                [hasContent]="true"
                [reportName]="title"
                [analyticsName]="analyticsName"
                [service]="apiService"
                [optionalExportApiServiceConfig]="optionalExportApiServiceConfig"
                [shareService]="shareReportService"
                [shareServiceFeatureFlag]="shareServiceFeatureFlag"
                [hideExport]="dataSource.noData$ | async"
                [hideDatePicker]="!(dataSource.data$ | async)"
                [datePickerConfiguration]="datePickerConfiguration"
            >
            </lsq-banner>

            <div class="container-fluid page-container">
                <lsq-report-actions-layout layoutWidth="sm">
                    <lsq-input
                        name="customer-name-filter"
                        [formControl]="customerNameFilterControl"
                        [prependIcon]="[icons.FaMagnifyingGlassSolid, color.GraphicGray]"
                        placeholder="Enter a customer name"
                    >
                    </lsq-input>
                </lsq-report-actions-layout>
                <div class="row">
                    <div class="col">
                        <lsq-data-table
                            *ngIf="!(responsiveService.smBelow | async)"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                            [hasFooter]="true"
                            [hasStickyHeader]="true"
                            (takeChanged)="handleTakeChange($event)"
                        >
                            <lsq-row-count-column></lsq-row-count-column>

                            <lsq-column
                                [column]="{
                                    header: 'Customer',
                                    key: 'debtorName',
                                    truncateTextCss: true,
                                    width: styling.TableColumn_TightWidth,
                                    className: 'customer-name-link'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <a
                                        [routerLink]="[
                                            getCustomerLink(
                                                agingByCustomerRouterLinkUrl,
                                                row.debtorKey
                                            )
                                        ]"
                                        [queryParams]="{ date: asOfDateQueryString }"
                                        class="preserve-case"
                                    >
                                        {{ row.debtorName }}
                                    </a>
                                </ng-template>
                                <ng-template [custom-footer-cell] let-row="row">
                                    <div class="grand-total">
                                        <span>Grand Total</span>
                                    </div>
                                </ng-template>
                            </lsq-column>

                            <lsq-currency-column
                                [column]="{
                                    header: 'Balance',
                                    key: 'balance'
                                }"
                                [totalValue]="(dataSource.data$ | async)?.totalBalance"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: 'Current',
                                    key: 'current'
                                }"
                                [totalValue]="(dataSource.data$ | async)?.totalCurrent"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: '1-30 Days',
                                    key: 'dayOne'
                                }"
                                [totalValue]="(dataSource.data$ | async)?.totalDayOne"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: '31-60 Days',
                                    key: 'dayThirtyOne'
                                }"
                                [totalValue]="(dataSource.data$ | async)?.totalDayThirtyOne"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: '61+ Days',
                                    key: 'daySixtyOne'
                                }"
                                [isMinZero]="true"
                                [totalValue]="(dataSource.data$ | async)?.totalDaySixtyOne"
                            >
                            </lsq-currency-column>
                        </lsq-data-table>

                        <lsq-data-table-mobile
                            *ngIf="responsiveService.smBelow | async"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                        >
                            <ng-template #mobileCardHeader let-row="row">
                                <div class="mtc-header-content">
                                    <div class="mtc-header-title">
                                        {{ row.debtorName }}
                                    </div>
                                    <div class="mtc-header-sub-title">
                                        Balance: {{ row.balance | lsqCurrency }}
                                    </div>
                                </div>
                            </ng-template>
                            <ng-template #mobileCardColumns let-row="row">
                                <div class="card-item">
                                    <div class="card-item-label">
                                        Current
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.current | lsqCurrency }}
                                    </div>
                                </div>
                                <div class="card-item">
                                    <div class="card-item-label">
                                        1-30 Days
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.dayOne | lsqCurrency }}
                                    </div>
                                </div>
                                <div class="card-item">
                                    <div class="card-item-label">
                                        31-60 Days
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.dayThirtyOne | lsqCurrency }}
                                    </div>
                                </div>
                                <div class="card-item">
                                    <div class="card-item-label">
                                        61+ Days
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.daySixtyOne | lsqCurrency }}
                                    </div>
                                </div>
                            </ng-template>
                        </lsq-data-table-mobile>
                    </div>
                </div>
            </div>
        </div>
    `
})
export class AgingReportComponent extends DateAsOfReportComponent implements OnDestroy {
    public title = 'Aging';
    public analyticsName = AnalyticsValue.Aging;
    public userStateType = ReportsUserStateTypes.GetAgingSummaryRequest;
    public savedDateFilterName = null;

    public optionalExportApiServiceConfig: IOptionalExportServiceConfig;
    public customerNameFilterControl = new FormControl();
    public asOfDateQueryString: string;

    // Constants:
    public agingByCustomerRouterLinkUrl = getRouterLinkUrlById(atlas, CUSTOMER_AGING);
    public classNames = ClassNames;
    public color = Color;
    public icons = Icons;
    public shareServiceFeatureFlag = FeatureFlags.ShareReport;
    public styling = Styling;
    public utilities = Utilities;

    public dataSource = new LsqApiServiceListSource<
        GetAgingSummaryResponse,
        GetAgingSummaryRequest,
        AgingSummary
    >(this.apiService);

    constructor(
        public apiService: GetAgingSummaryService,
        public agingDetailReportService: GenerateAgingDetailReportService,
        public shareReportService: ShareReportService,
        public responsiveService: ResponsiveService,
        public injector: Injector
    ) {
        super(injector);

        this.dispatchInitialRequest();

        this.dataSource.data$
            .pipe(forLifeOf(this))
            .subscribe((response: GetAgingSummaryResponse) => {
                this.asOfDateQueryString = Utilities.getISOStringWithoutTimezone(response.asOfDate);
            });

        this.customerNameFilterControl.valueChanges
            .pipe(
                forLifeOf(this),
                distinctUntilChanged(),
                // Ignore the debounceTime when clearing the search.
                tap((customerNameSearch) => {
                    if (!customerNameSearch) {
                        this.dataSource.dispatchRequest({
                            customerSearchFilter: null
                        });
                    }
                }),
                debounceTime(Interaction.SearchDebounceTimeMs),
                filter(
                    (customerNameSearch) =>
                        customerNameSearch &&
                        customerNameSearch.length >= Interaction.MinTextSearchLength
                )
            )
            .subscribe((customerNameSearch) => {
                this.dataSource.dispatchRequest({
                    skip: 0,
                    customerSearchFilter: customerNameSearch
                });
            });

        this.optionalExportApiServiceConfig = {
            label: 'Include Invoice Detail',
            service: this.agingDetailReportService,
            endpointName: this.agingDetailReportService.endpointName,
            dataSource: this.dataSource
        };
    }

    public ngOnDestroy(): void {}

    public getCustomerLink(link: string, debtorKey: string): string {
        return getDynamicRouteLink(link, { debtorKey });
    }
}
